/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import G17Client from '../../../../services/G17Client';
import { nl2br } from '../../../../utils';
import { AIResponse } from '../../../../types/ai';
import { AILoadingPlaceholder } from './AILoadingPlaceholder';
import { AIQuestionAndAnswer } from './AIQuestionAndAnswer';
import { useTypewriter } from '../../hooks/useTypewriter';
import { ButtonGradient } from '../ButtonGradient/ButtonGradient';
import { QUESTION } from '../../../../constants/terminology';

interface AINarrativeProps {
  initiativeId: string;
  surveyId: string;
}

export const AINarrative = ({ initiativeId, surveyId }: AINarrativeProps) => {

  const [isLoading, setLoading] = useState(false);
  const [aiResponse, setAIResponse] = useState<AIResponse | undefined>();

  const typewriter = useTypewriter({
    appendStr: aiResponse?.content ?? ''
  });

  const isWriting = typewriter.isWriting;
  const response = isWriting ? typewriter.liveResult : typewriter.finalResult;

  const generateNarrative = () => {
    setLoading(true);
    G17Client.getSurveyNarrative(initiativeId, surveyId)
      .then(setAIResponse)
      .finally(() => setLoading(false));
  }

  const showDisclaimer = isLoading || isWriting || response;
  const questionAndAnswerProps = {
    initiativeId, surveyId
  };
  return (
    <>
      <h5 className='mt-3'>SDG Contribution Summary</h5>
      <AILoadingPlaceholder isWriting={isLoading || isWriting}>
        <div className='w-100 position-relative border border-ThemeBorderDefault background-ThemeBgExtralight border-radius pb-6'>
          <div
            className='w-100 p-3'
            style={{ minHeight: '100px', maxHeight: '300px', overflow: 'auto' }}>
            {isLoading
              ? <span className='text-gradient'>Thinking...</span>
              :
              response ? nl2br(response) : ''
            }
          </div>
          <div className='position-absolute' style={{ bottom: '10px', right: '10px' }}>
            <ButtonGradient outline disabled={isLoading || isWriting} onClick={() => generateNarrative()}>
              <i className='fal fa-sparkles mr-2' />Generate
            </ButtonGradient>
          </div>
        </div>
      </AILoadingPlaceholder>
      <div className='p-2 text-center text-ThemeTextPlaceholder'>
        <i className='fal fa-sparkles mr-2' />
        AI generated drafts are for guidance only and should be carefully checked and edited by a human to ensure they are factual.
      </div>

      <h5 className='mt-3'>{QUESTION.CAPITALIZED_PLURAL} and Answers</h5>
      <AIQuestionAndAnswer {...questionAndAnswerProps} defaultQuestion='What is my overall SDG contribution?' />
      <AIQuestionAndAnswer {...questionAndAnswerProps} defaultQuestion='What is my most important SDG, and how can I improve it?' />
      <AIQuestionAndAnswer {...questionAndAnswerProps} defaultQuestion='What is my worst performing SDG, and how can I improve it?' />

      <h5 className='mt-3'>Ask a {QUESTION.SINGULAR}</h5>
      <AIQuestionAndAnswer {...questionAndAnswerProps} />
      {showDisclaimer ?
        <div className='p-2 text-center text-ThemeTextPlaceholder'>
          <i className='fal fa-sparkles mr-2' />
          AI generated drafts are for guidance only and should be carefully checked and edited by a human to ensure they are factual.
        </div> : null}
    </>
  );
};
